/* @flow */
import React from 'react';
import { autorun } from 'mobx';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

import { APPLICATION_INSIGHTS_KEY, ENV, VERSION_NUMBER, BUILD_NUMBER } from 'config';
import { mrrEnhancedHistory, userStore } from 'services/ServiceRegistry';

import styles from './AIError.module.scss';

export const reactPlugin = new ReactPlugin();

const isTestEnvironment = ['development'].includes(ENV);

export const appInsights =
  APPLICATION_INSIGHTS_KEY &&
  new ApplicationInsights({
    config: {
      instrumentationKey: APPLICATION_INSIGHTS_KEY,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { browserHistory: mrrEnhancedHistory },
      },
      enableAutoRouteTracking: true,
      // None of our owned dependencies are on a different domain, so we don't need to correlate CORS requests.
      // We also can't, because some of our extenal dependencies do not support additional headers in the CORS preflight (Maps, Analytics, etc)
      enableCorsCorrelation: false,
      enableUnhandledPromiseRejectionTracking: true,
      enableAjaxPerfTracking: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,
      enableDebug: isTestEnvironment,
      enableDebugExceptions: isTestEnvironment,
      loggingLevelTelemetry: 0,
    },
  });

const sensitivityTelemetryInitializer = (item: ITelemetryItem) => {
  if (
    item.baseType === 'RemoteDependencyData' &&
    item.baseData &&
    item.baseData.properties &&
    item.baseData.properties.requestHeaders
  ) {
    delete item.baseData.properties.requestHeaders.authorization;
    delete item.baseData.properties.requestHeaders['b2c-token'];
  }
};

const init = () => {
  if (appInsights) {
    appInsights.loadAppInsights();
    appInsights.addTelemetryInitializer(sensitivityTelemetryInitializer);
    appInsights.context.application.ver = VERSION_NUMBER;
    appInsights.context.application.build = BUILD_NUMBER;

    // monkeyPatchConsole();

    setImmediate(() => {
      autorun(() => {
        if (
          userStore &&
          userStore.userLoaded &&
          userStore.activeUser &&
          userStore.activeUser.id !== appInsights.context.user.authenticatedId
        ) {
          appInsights.setAuthenticatedUserContext(userStore.activeUser.id);
        }
      });
    });
  }
};

const monkeyPatchConsole = () => {
  if (appInsights) {
    const _oldConsole = { ...console };

    const patchMethod = (fn: Function, severityLevel: SeverityLevel) =>
      function(...args) {
        fn.apply(this, args);

        const message = args
          .map(arg => (typeof arg !== 'string' ? JSON.stringify(arg) : arg))
          .join(' ');

        switch (severityLevel) {
          case SeverityLevel.Critical:
          case SeverityLevel.Error:
            appInsights.trackException(
              { exception: new Error(message), severityLevel },
              { source: 'console' }
            );
            break;

          default:
          case SeverityLevel.Warning:
          case SeverityLevel.Information:
          case SeverityLevel.Verbose:
            appInsights.trackTrace({ message, severityLevel });
            break;
        }
      };

    const newConsole: typeof _oldConsole = {
      ..._oldConsole,
      log: patchMethod(_oldConsole.log, SeverityLevel.Information),
      debug: patchMethod(_oldConsole.log, SeverityLevel.Verbose),
      info: patchMethod(_oldConsole.info, SeverityLevel.Information),
      warn: patchMethod(_oldConsole.warn, SeverityLevel.Warning),
      error: patchMethod(_oldConsole.error, SeverityLevel.Error),
    };

    window.console = newConsole;
  }
};

export class AIErrorBoundary extends React.PureComponent {
  render() {
    const { children } = this.props;

    if (!appInsights) {
      return children;
    }

    return (
      <AppInsightsErrorBoundary
        appInsights={reactPlugin}
        onError={() =>
          console.log(appInsights) || (
            <div className={styles.AIErrorWrapper}>
              <div>
                <h2>Unknown Error</h2>
                <p>
                  Please refresh the page or try again later. We're working on fixing the problem.
                </p>
                {appInsights && (
                  <p className={styles.appInsightsInfo}>
                    <b>Session ID:</b>{' '}
                    <code>{appInsights.context.sessionManager.automaticSession.id}</code>
                    <br />
                    <b>Operation ID:</b> <code>{appInsights.context.telemetryTrace.traceID}</code>
                  </p>
                )}
              </div>
            </div>
          )
        }
      >
        {children}
      </AppInsightsErrorBoundary>
    );
  }
}

init();
